import { useEffect, useState } from 'react';

type PanelTransitionEvent = {
  currentTarget: EventTarget;
  propertyName: string;
  target: EventTarget;
};

/**
 * Keeps panel content mounted while a slide-out animation finishes.
 * Updates `active` immediately when opening; clears it only after the panel's
 * own transform transition ends while closed. Ignoring bubbled child
 * transitions prevents nested animations from unmounting the panel early.
 */
export function useRetainedPanelContent<T>(data: T | null) {
  const isOpen = data !== null;
  const [active, setActive] = useState<T | null>(data);

  useEffect(() => {
    if (data) {
      setActive(data);
    }
  }, [data]);

  function handleTransitionEnd(event: PanelTransitionEvent): boolean {
    // The transform is the panel's slide-out animation. Width and nested
    // transitions can finish independently and must not unmount its content.
    const slideOutFinished =
      !isOpen &&
      event.target === event.currentTarget &&
      event.propertyName === 'transform';

    if (slideOutFinished) {
      setActive(null);
      return true;
    }

    return false;
  }

  return { active, handleTransitionEnd, isOpen };
}
